// Screen 19 · People and permissions (Settings → Users): one grid. People down the side, the areas across,
// one level in each cell: None, See, Suggest, Change, Manage (2026-10-23, docs/ai-employee-needs-brief.md: Change edits what
// exists; Manage also creates and retires; an AI login's cell goes up to the area's ai_most cap). A role is a preset that fills a row; a cell is a
// per-person change, as before. The permission keys stay as the machinery underneath (each area and level
// maps to the keys it covers; the mapping is data), and the screen never shows an individual key
// (docs/ai-connections-brief.md, Part 2 outcome a). Pipeline is five action columns under one header (permission_area.group;
// the sheet, section 3, round 4), and a script key is a row like any other login (user.is_script), 2026-10-19.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LEVEL_LABELS, areaLevel, cellOverrides, keysForLevel, keysUpTo, levelsOffered, type AreaKeyRow, type AreaLevel } from '@zerogap/engine';
import { deleteRow, insertRow, refresh, toast, updateRow, useRows, type Row } from '../lib/api';
import { ROLES } from '../lib/session';
import { REVIEW, supabase } from '../lib/supabase';
import { Btn, Edit, Field, Head, Info, Loading, Modal, Panel, Tag } from '../ui/kit';

const AI_PRESET = 'AI employee';

export function Permissions() {
  const users = useRows('user', { order: ['created_at'] });
  const perms = useRows('permission', { order: ['sort'] });
  const rolePerms = useRows('role_permission');
  const userPerms = useRows('user_permission');
  const areas = useRows('permission_area', { order: ['sort'] });
  const areaKeys = useRows('permission_area_key');
  const presets = useRows('area_preset');
  /** A key just issued, shown once; or the person whose key must be issued from the computer (local review has no owner sign-in). */
  const [issued, setIssued] = useState<{ name: string; email: string; key: string } | null>(null);
  const [howTo, setHowTo] = useState<Row | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  if (users.loading || perms.loading || areas.loading || areaKeys.loading || rolePerms.loading || userPerms.loading || presets.loading) return <Loading />;

  const map = areaKeys.rows as AreaKeyRow[];
  const keyOf = new Map(perms.rows.map((p) => [p.id as string, p.key as string]));
  const idOf = new Map(perms.rows.map((p) => [p.key as string, p.id as string]));
  const roleGranted = (role: string) => new Set(rolePerms.rows.filter((x) => x.role === role).map((x) => keyOf.get(x.permission_id) ?? ''));
  const personGranted = (u: Row) => {
    const set = roleGranted(u.role);
    for (const up of userPerms.rows) {
      if (up.user_id !== u.id) continue;
      const key = keyOf.get(up.permission_id) ?? '';
      if (up.granted) set.add(key);
      else set.delete(key);
    }
    return set;
  };
  const ownRows = (u: Row, area: string) => {
    const covered = new Set(keysUpTo(map, area, 'change'));
    return userPerms.rows.filter((x) => x.user_id === u.id && covered.has(keyOf.get(x.permission_id) ?? ''));
  };

  /** One cell: the person's own rows become exactly what the level needs beyond their role, and nothing else. */
  const setCell = async (u: Row, area: string, level: AreaLevel) => {
    const wanted = cellOverrides(map, area, level, roleGranted(u.role));
    const have = ownRows(u, area);
    for (const row of have) {
      const key = keyOf.get(row.permission_id) ?? '';
      const w = wanted.find((x) => x.key === key);
      if (!w) await deleteRow('user_permission', row.id);
      else if (w.granted !== row.granted) await updateRow('user_permission', row.id, { granted: w.granted });
    }
    for (const w of wanted) {
      if (!have.some((row) => keyOf.get(row.permission_id) === w.key)) await insertRow('user_permission', { user_id: u.id, permission_id: idOf.get(w.key), granted: w.granted });
    }
  };
  /** A role's own cell: the keys the level needs granted to the role, those over it taken away. */
  const setRoleCell = async (role: string, area: string, level: AreaLevel) => {
    const { grant, revoke } = keysForLevel(map, area, level);
    for (const key of grant) {
      if (!rolePerms.rows.some((x) => x.role === role && keyOf.get(x.permission_id) === key)) await insertRow('role_permission', { role, permission_id: idOf.get(key) });
    }
    for (const key of revoke) {
      const row = rolePerms.rows.find((x) => x.role === role && keyOf.get(x.permission_id) === key);
      if (row) await deleteRow('role_permission', row.id);
    }
  };
  const presetLevels = (name: string): Record<string, AreaLevel> => {
    if ((ROLES as readonly string[]).includes(name)) {
      const g = roleGranted(name);
      return Object.fromEntries(areas.rows.map((a) => [a.key, name === 'owner' ? 'manage' : areaLevel(map, a.key, g).level]));
    }
    return Object.fromEntries(areas.rows.map((a) => [a.key, (presets.rows.find((p) => p.name === name && p.area_key === a.key)?.level ?? 'none') as AreaLevel]));
  };
  const setPresetCell = async (name: string, area: string, level: AreaLevel) => {
    const row = presets.rows.find((p) => p.name === name && p.area_key === area);
    if (row) await updateRow('area_preset', row.id, { level });
    else await insertRow('area_preset', { name, area_key: area, level });
  };
  const fill = async (u: Row, name: string) => {
    const levels = presetLevels(name);
    for (const a of areas.rows) await setCell(u, a.key, levels[a.key] ?? 'none');
    toast(`${u.name}'s row filled from ${name}.`);
  };
  const presetNames = [...ROLES.filter((r) => r !== 'owner'), ...new Set(presets.rows.map((p) => p.name as string))];
  // the header row over the areas: one cell per run of areas that share a group (Pipeline), an empty one for the rest
  const groupRow = areas.rows.reduce<{ group: string | null; n: number }[]>((acc, a) => {
    const g = (a.group as string | null) ?? null;
    const last = acc[acc.length - 1];
    if (last && last.group === g && g !== null) last.n++;
    else acc.push({ group: g, n: 1 });
    return acc;
  }, []);
  const grouped = groupRow.some((g) => g.group !== null);
  const GroupHead = ({ before, after }: { before: number; after: number }) =>
    grouped ? (
      <tr className="grouphead">
        <th colSpan={before} style={{ borderBottom: 0 }} />
        {groupRow.map((g, i) => (
          <th key={i} colSpan={g.n} className={g.group ? 'pgrp' : ''} style={g.group ? undefined : { borderBottom: 0 }}>
            {g.group ?? ''}
          </th>
        ))}
        {after > 0 && <th colSpan={after} style={{ borderBottom: 0 }} />}
      </tr>
    ) : null;

  // the owner issues an AI employee its key: the sign-in-key function makes or renews the login and hands the key back once
  const issue = async (u: Row) => {
    if (!u.email) {
      toast('Type an email first: it is the sign-in name.');
      return;
    }
    if (REVIEW) {
      setHowTo(u);
      return;
    }
    setBusy(u.id);
    try {
      const { data, error } = await supabase.functions.invoke('sign-in-key', { body: { user_id: u.id } });
      if (error) {
        let m = error.message;
        try {
          const body = await (error as { context?: Response }).context?.json();
          if (body?.message) m = body.message;
        } catch {
          /* the message as it came */
        }
        toast(m.includes('Failed to send') || m.includes('not found') ? 'The sign-in-key function is not deployed yet. Until it is, a key is issued from the computer (see the report).' : `Could not issue a key: ${m}`);
        return;
      }
      setIssued(data as { name: string; email: string; key: string });
      refresh('user'); // the row is linked to its login now, so "has a login" shows
    } finally {
      setBusy(null);
    }
  };

  /** Change and Manage are offered to an AI login or a preset, up to the area's cap (ai_most: Instructions stops at Suggest); a person's cell shows them only where held, since the database lets only the owner write (2026-10-22, Manage 2026-10-23). */
  const LevelPick = ({ value, partial, own, onPick, title, ai, aiMost }: { value: AreaLevel; partial: boolean; own: boolean; onPick: (l: AreaLevel) => void; title: string; ai: boolean; aiMost?: AreaLevel | null }) => (
    <span className="nowrap">
      <select className={own ? 'ov' : ''} value={value} title={`${title} None: nothing. See: reads. Suggest: files a suggestion for Spencer. Change: edits what exists, at once, unverified until verified. Manage: also creates and retires (never deletes).`} onChange={(e) => onPick(e.target.value as AreaLevel)}>
        {levelsOffered({ ai, held: value, aiMost }).map((l) => (
          <option key={l} value={l}>
            {LEVEL_LABELS[l]}
          </option>
        ))}
      </select>
      {partial && (
        <small className="muted" title="Holds some of this area's screens, not all: set before the grid existed. Pick a level to settle it.">
          {' '}
          part
        </small>
      )}
    </span>
  );

  return (
    <div className="stack">
      <Head title={`Users · ${users.rows.length}`} sub="Every person, AI employee and script has a login. Each cell is their level in one area; a role fills the row, a cell is a change for that one login.">
        <Btn onClick={() => void insertRow('user', { name: 'New person', role: 'shop' })}>+ Person</Btn>
      </Head>
      <Panel>
        <div className="tblwrap">
          <table>
            <thead>
              <GroupHead before={5} after={1} />
              <tr>
                <th>Person</th>
                <th>Email (their sign-in)</th>
                <th>Sign-in</th>
                <th>Role</th>
                <th>Active</th>
                {areas.rows.map((a) => (
                  <th key={a.key} title={a.covers ?? ''} className={a.group ? 'psub' : ''}>
                    {a.label}
                  </th>
                ))}
                <th>Fill row from</th>
              </tr>
            </thead>
            <tbody>
              {users.rows.map((u) => {
                const granted = personGranted(u);
                // what the grid lets an AI login do through its connector (2026-10-22): the highest level it holds in any area
                const held = areas.rows.map((a) => areaLevel(map, a.key, granted).level);
                const writes = u.role === 'owner' ? 'owner' : held.includes('manage') ? 'manages' : held.includes('change') ? 'edits at Change' : held.includes('suggest') ? 'suggests' : 'read only';
                return (
                  <tr key={u.id}>
                    <td style={{ minWidth: 150 }}>
                      <Edit className="cell" value={u.name} onSave={(v) => updateRow('user', u.id, { name: v || u.name })} />
                      <small>{u.is_script ? 'script key' : u.is_ai ? `AI employee${writes === 'read only' ? ' · read only' : ''}` : 'by email link'}</small>
                    </td>
                    <td style={{ minWidth: 200 }}>
                      <Edit className="cell" type="email" value={u.email ?? ''} placeholder="email" onSave={(v) => updateRow('user', u.id, { email: v.trim().toLowerCase() || null })} />
                      {u.auth_user_id ? <small>has a login</small> : <small>no login yet</small>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {u.is_ai ? (
                        <>
                          <Btn small disabled={busy === u.id || !u.active} title={u.active ? 'Makes this employee a login, or replaces its key with a new one. The key shows once.' : 'Tick Active first.'} onClick={() => void issue(u)}>
                            {u.auth_user_id ? 'New key' : 'Issue key'}
                          </Btn>
                          <br />
                          <small>
                            by key · <Tag kind="off" title="What the grid lets this login do through its connector: edit directly where a cell says Change, suggest where it says Suggest, read only otherwise.">{writes}</Tag>
                          </small>
                        </>
                      ) : (
                        <small>by email link{u.role !== 'owner' ? ' · read only' : ''}</small>
                      )}
                      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                        <label className="check">
                          <input type="checkbox" checked={!!u.is_ai} onChange={(e) => void updateRow('user', u.id, { is_ai: e.target.checked })} /> <small>AI</small>
                        </label>
                        {u.is_ai && (
                          <label className="check" title="A script key: the email matcher, the Concept Board Worker, the transcript router, the Landing Board. Its cells are the one or two its calls need.">
                            <input type="checkbox" checked={!!u.is_script} onChange={(e) => void updateRow('user', u.id, { is_script: e.target.checked })} /> <small>script</small>
                          </label>
                        )}
                      </div>
                    </td>
                    <td>
                      <select value={u.role} onChange={(e) => void updateRow('user', u.id, { role: e.target.value })}>
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input type="checkbox" checked={!!u.active} onChange={(e) => void updateRow('user', u.id, { active: e.target.checked })} style={{ width: 18, height: 18 }} />
                    </td>
                    {areas.rows.map((a) => {
                      if (u.role === 'owner') {
                        return (
                          <td key={a.key} style={{ textAlign: 'center' }}>
                            <Tag kind="on">owner</Tag>
                          </td>
                        );
                      }
                      const st = areaLevel(map, a.key, granted);
                      const own = ownRows(u, a.key).length > 0;
                      return (
                        <td key={a.key}>
                          <LevelPick value={st.level} partial={st.partial} own={own} ai={!!u.is_ai} aiMost={(a.ai_most as AreaLevel | null) ?? null} title={own ? `${a.label}: set for ${u.name}. ${a.covers ?? ''}` : `${a.label}: the ${u.role} role's level. ${a.covers ?? ''}`} onPick={(l) => void setCell(u, a.key, l)} />
                        </td>
                      );
                    })}
                    <td>
                      {u.role !== 'owner' && (
                        <select value="" onChange={(e) => e.target.value && void fill(u, e.target.value)}>
                          <option value="">preset…</option>
                          {presetNames.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <small>
          An amber cell is set for that one login; the rest follow the role. <b>See</b> reads; <b>Suggest</b> files a suggestion for you; <b>Change</b> edits what exists at once, unverified until you verify it; <b>Manage</b> also creates (a job, an item, a room, a build, a package, a catalog item) and retires, with Restore on the row. <i>part</i>: set before the grid; pick a level to settle it.
        </small>
      </Panel>

      <Panel label="Presets: what a role gives, and the AI rows" right={<Info left>A role's row is what every person of that role has before any change of their own; pick a cell to change the role. The AI employee row is the worker pattern (Manage on Job items, Builds & add-ons, Scope rows, Prices and Library, Change on Process; See · Change · Change · See on the rest of the Pipeline, since 2026-10-23); AI pipeline manager is Change on all five Pipeline columns (Alex, Atlas); script is None everywhere, and each script key then gets the one or two cells its calls need. The preset is the starting point: you set every AI login's cells yourself. Fill a row from one, then adjust. The owner is the owner everywhere and never appears here.</Info>}>
        <div className="tblwrap">
          <table>
            <thead>
              <GroupHead before={1} after={0} />
              <tr>
                <th>Preset</th>
                {areas.rows.map((a) => (
                  <th key={a.key} title={a.covers ?? ''} className={a.group ? 'psub' : ''}>
                    {a.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {presetNames.map((name) => {
                const levels = presetLevels(name);
                const isRole = (ROLES as readonly string[]).includes(name);
                return (
                  <tr key={name}>
                    <td>
                      <b>{name}</b> <small>{isRole ? 'role' : 'preset'}</small>
                    </td>
                    {areas.rows.map((a) => {
                      const st = isRole ? areaLevel(map, a.key, roleGranted(name)) : { level: levels[a.key] ?? 'none', partial: false };
                      return (
                        <td key={a.key}>
                          <LevelPick value={st.level} partial={st.partial} own={false} ai={!isRole} aiMost={(a.ai_most as AreaLevel | null) ?? null} title={a.covers ?? ''} onPick={(l) => void (isRole ? setRoleCell(name, a.key, l) : setPresetCell(name, a.key, l))} />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      <small>
        <Tag kind="on">enforced in the database</Tag> Every level is enforced for every AI login; only a person ever sets Verified. A refused connector call lands on the <Link to="/settings?view=changes">Change log</Link> as Blocked, under the employee's name.{' '}
        <Info left>
          See: a login reads only the tables its areas open; reading is per table, so Prices at None beside Jobs at See hides the price screens but not the material prices a job carries. Suggest, Change and Manage on Job items, Builds & add-ons, Scope rows, Prices and Library: a suggestion goes in only where the row says Suggest or more, a direct edit only where it says Change or Manage, a create or a retire only at Manage, and the edit stays unverified until you verify it. Verify and Checked, money, delete, lock, publish, approve, people and settings stay yours whatever a cell says (the owner-only list under Settings → AI employees). A person's writes stay the owner's. Change on the five Pipeline columns is enforced from the pipeline's Part 3. People and settings, Verify and Money are the owner's alone and are not on the grid; the Process column is the one carve-out, so the Pipeline manager can edit Pipeline steps without the owner. Untick Active and that login reads nothing at once. A person signs in with a link sent to the email here; an AI employee signs in with a key issued here, or through the ZeroGap connector at its own address; a new key replaces the old one and is shown only when issued.
        </Info>
      </small>
      {issued && (
        <Modal title={`Sign-in key for ${issued.name}`} onClose={() => setIssued(null)}>
          <div className="stack">
            <p>
              This key is shown <b>once</b>. Copy it now to where {issued.name} keeps its configuration. If it is lost, issue a new one; the old one stops working.
            </p>
            <Field label="Email (the sign-in name)">
              <input readOnly value={issued.email} onFocus={(e) => e.currentTarget.select()} />
            </Field>
            <Field label="Key">
              <input readOnly value={issued.key} onFocus={(e) => e.currentTarget.select()} style={{ fontFamily: 'ui-monospace, monospace' }} />
            </Field>
            <div>
              <Btn
                primary
                onClick={() => {
                  void navigator.clipboard.writeText(issued.key).then(
                    () => toast('Key copied.'),
                    () => toast('Could not copy; select the key and copy it by hand.'),
                  );
                }}
              >
                Copy the key
              </Btn>
            </div>
          </div>
        </Modal>
      )}
      {howTo && (
        <Modal title={`A key for ${howTo.name}`} onClose={() => setHowTo(null)}>
          <div className="stack">
            <p>Local review has no owner sign-in, so a key cannot be issued from here. From the repo folder, in a terminal:</p>
            <pre style={{ whiteSpace: 'pre-wrap' }}>{`pnpm sign-in-key ${howTo.email}`}</pre>
            <p>
              <small>The key is printed there once. On the published app this button issues it directly.</small>
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}
